import { useQuery } from "@tanstack/react-query";

/** The api's view of the browser's X (Twitter) sign-in, carried by an
 * HttpOnly session cookie set by the OAuth callback. */
export interface CompetitionSession {
  authenticated: boolean;
  handle?: string;
  xUserId?: string;
}

export const competitionSessionKey = ["competition", "session"] as const;

/** Poll the current X sign-in state. Refetches on window focus so returning
 * from the OAuth redirect picks the new session up without a reload. */
export const useCompetitionSession = () => {
  return useQuery<CompetitionSession>({
    queryKey: competitionSessionKey,
    queryFn: async () => {
      const res = await fetch(`${window.__APP_CONFIG.apiBaseUrl}/auth/session`, {
        credentials: "include",
      });
      if (!res.ok) return { authenticated: false };
      const body = (await res.json()) as {
        authenticated: boolean;
        handle?: string;
        x_user_id?: string;
      };
      return {
        authenticated: body.authenticated,
        handle: body.handle,
        xUserId: body.x_user_id,
      };
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
};

/** Full-page redirect into the api's X OAuth flow. return_to brings the
 * browser back to THIS origin (validated server-side against an allowlist),
 * so the flow works from preview deploys too. */
export const beginXSignIn = () => {
  const returnTo = encodeURIComponent(window.location.origin);
  window.location.assign(
    `${window.__APP_CONFIG.apiBaseUrl}/auth/x/login?return_to=${returnTo}`,
  );
};

/** Tie the submitted deposit to the registration, so a deposit that expires
 * unabsorbed releases the X-account binding for another attempt. Best-effort:
 * a failure here never blocks the deposit itself. */
export const recordDepositSubmitted = async (requestId: number): Promise<void> => {
  try {
    await fetch(`${window.__APP_CONFIG.apiBaseUrl}/competition/deposit-submitted`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ request_id: requestId }),
    });
  } catch {
    /* recorded on a later attempt or resolved by the grace window */
  }
};

/** Clear the session cookie. Callers should invalidate the session query. */
export const signOutX = async (): Promise<void> => {
  await fetch(`${window.__APP_CONFIG.apiBaseUrl}/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
};

/** Record the participant's registration and rules agreement. The api binds
 * the signed-in X identity to the deposit account and stores the agreement
 * (rules hash, ip, timestamp). Errors carry the api's message (e.g. the X
 * account is already registered to a different deposit account). */
export const registerCompetitionAccount = async (input: {
  accountId: string;
  rulesSha256: string;
}): Promise<void> => {
  const res = await fetch(
    `${window.__APP_CONFIG.apiBaseUrl}/competition/register`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        account_id: input.accountId,
        rules_sha256: input.rulesSha256,
      }),
    },
  );
  if (!res.ok) {
    let message = `registration failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* non-json error body */
    }
    throw new Error(message);
  }
};
